import { useState, useEffect, useContext, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Collapse,
  Fade,
  TextField as MuiTextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select as MuiSelect,
  Chip,
  Checkbox as MuiCheckbox,
  ListItemText,
  IconButton,
  Tooltip,
  Menu,
  Switch,
  Divider,
} from "@mui/material";
import SplitPanel from "components/details-page/SplitPanel";
import useIsMobile from "components/hooks/useIsMobile";
import {
  ChevronDown,
  Users,
  Copy,
  Download,
  Save,
  Trash2,
  MoreVertical,
  SquarePen,
  Pencil,
  MessagesSquare,
} from "lucide-react";
import { useRunWorkflow } from "services/workflow";
import {
  RunWorkflowRequest,
  RunWorkflowResponse,
} from "protogen/workflow_service_pb";
import { CurrentUserContext } from "components/context/RequireAuth";
import { isUserAdmin } from "common/userUtils";
import FamilySelect from "components/creation/FamilySelect";
import AdminFamilySelect from "components/creation/AdminFamilySelect";
import { ReactComponent as SparklesIcon } from "../../../icons/Advisor/Sparkles.svg";
import { useSnackpack } from "components/context/SnackpackContextProvider";
import DocumentViewer from "components/editor/DocumentViewer";
import {
  LoadDocument,
  LoadDocumentMetadata,
} from "components/creation/LoadDocument";
import { useCreateDocument } from "services/documents";
import { DocumentType } from "protogen/documents_pb";
import FlexBox from "components/common/FlexBox";
import ThreeDotsActiveButton from "components/common/ThreeDotsActiveButton";
import {
  useListAgentPrompts,
  useCreateAgentPrompt,
  useUpdateAgentPrompt,
  useToggleAgentPrompt,
} from "services/agents";
import {
  AgentPrompt,
  ListAgentPromptsRequest,
  UpdateAgentPromptRequest,
} from "protogen/agents_service_pb";
import TruncatedText from "components/common/TruncatedText";
import Select from "components/common/Select";
import DateDisplay from "components/common/DateDisplay";
import MiniFeed from "components/forum/MiniFeed";
import { ActivityEntityType } from "protogen/common_pb";

const EXAMPLE_PROMPTS = [
  {
    title: "Weekly check-in",
    prompt:
      "Populate this document with updated information based on today's date and completed tasks from previous week. If there are no tasks that have been marked as done from the past week, you can include tasks that are in progress, but make sure to emphasize they are in progress.",
  },
  {
    title: "Task ideas",
    prompt: `Create a document with tasks and ideas relevant to this family.

For seasonal -- Include task ideas that seem in line with what this family typically asks for, and/or what most families like theirs might want help in current season. This could be across home, travel, family, shopping, events, etc. Include at least 5 items. Don't suggest things we've talked about in the past 90 days.

For upcoming event support -- Look at their calendar or comms to see what events are after today, and come up with ideas of how the assistant could help. Include at least 5 items.

For recurring/annual tasks -- Look at past completed tasks at same time period the year before. Resurface here if relevant.`,
  },
  {
    title: "Family dossier",
    prompt: `Create a comprehensive dossier about this family based on all available information including communications, tasks, facts, notes, and events.

Please use this template structure:
- Family overview and members
- Key preferences and interests
- Important dates and recurring events
- Communication preferences
- Notable accomplishments and milestones

For missing details, do not suggest additional family members if they've never been mentioned.`,
  },
  {
    title: "Goal progress",
    prompt:
      "Create a supportive message that reviews progress toward goals, acknowledges effort, and provides gentle guidance for next steps.",
  },
  {
    title: "Celebration note",
    prompt:
      "Write an uplifting message celebrating recent accomplishments and milestones, highlighting specific achievements and their impact.",
  },
];

type ModelCost = {
  name: string;
  label: string;
};

const MODELS: ModelCost[] = [
  { name: "gpt-4o", label: "gpt-4o" },
  { name: "gpt-4o-mini", label: "gpt-4o-mini" },
  { name: "gpt-4.1", label: "gpt-4.1" },
  { name: "gpt-4.1-mini", label: "gpt-4.1-mini" },
  { name: "gpt-4.1-nano", label: "gpt-4.1-nano" },
  { name: "gpt-5", label: "GPT5" },
  { name: "gpt-5-mini", label: "GPT5 Mini" },
  { name: "gpt-5-nano", label: "GPT5 Nano" },
  { name: "o4-mini", label: "o4-mini (thinking)" },
  { name: "o3", label: "o3 (expensive)" },
];

type PromptConfiguration = {
  includeTasks: boolean;
  includeFacts: boolean;
  includeNotes: boolean;
  includeTaskActivity: boolean;
  eventsWeeksForward: number;
  activityWeeksBack: number;
  communicationDaysBack?: number; // legacy field for backwards compatibility
  model?: string;
  documentId?: string;
};

const DEFAULT_CONFIGURATION: PromptConfiguration = {
  includeTasks: true,
  includeFacts: true,
  includeNotes: true,
  includeTaskActivity: true,
  eventsWeeksForward: 0,
  activityWeeksBack: 4,
};

const ConfigurationSection = ({
  title,
  description,
  content,
}: {
  title: string;
  description?: string;
  content: React.ReactNode;
}) => {
  const formLeftStyle = { flex: 2 };
  const formRightStyle = { flex: 5 };
  const isMobile = useIsMobile();

  return (
    <FlexBox direction={isMobile ? "column" : "row"} alignItems="flex-start">
      <FlexBox direction="column" gap="2px" sx={{ ...formLeftStyle }}>
        <Typography
          variant="bodyHeavy"
          sx={{
            marginTop: isMobile ? "0" : "16px",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </Typography>
        {description && (
          <Typography variant="bodySmall" sx={{ paddingRight: "4px" }}>
            {description}
          </Typography>
        )}
      </FlexBox>
      <FlexBox sx={{ ...formRightStyle }}>{content}</FlexBox>
    </FlexBox>
  );
};

const runWorkflowWithPolling = async (
  workflowRequest: (
    payload: RunWorkflowRequest,
  ) => Promise<RunWorkflowResponse | null>,
  request: RunWorkflowRequest,
  pollIntervalMsStart: number = 500,
  pollIntervalMsMax: number = 2500,
  maxPollTimeMs: number = 120000,
): Promise<string | undefined> => {
  const asyncRequest = new RunWorkflowRequest({
    ...request,
    runAsync: true,
  });

  await workflowRequest(asyncRequest);

  const cacheRequest = new RunWorkflowRequest({
    ...request,
    onlyUseCache: true,
  });

  const startTime = Date.now();
  let pollIntervalMs = pollIntervalMsStart;
  while (Date.now() - startTime < maxPollTimeMs) {
    const response = await workflowRequest(cacheRequest);

    if (response?.result && response.result !== "") {
      return response.result;
    }
    pollIntervalMs = Math.min(pollIntervalMs * 1.1, pollIntervalMsMax);
    await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
  }

  throw new Error(`Workflow timed out after ${maxPollTimeMs}ms`);
};

export default () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const currentUser = useContext(CurrentUserContext);
  const isAdmin = isUserAdmin(currentUser);
  const { pushSnack } = useSnackpack();
  const [searchParams, setSearchParams] = useSearchParams();

  // Prompts list state
  const [prompts, setPrompts] = useState<AgentPrompt[]>([]);
  const [selectedPrompt, setSelectedPrompt] = useState<AgentPrompt | null>(
    null,
  );
  const [promptText, setPromptText] = useState("");
  const [promptName, setPromptName] = useState("");
  const [promptScope, setPromptScope] = useState("public");
  const [promptModified, setPromptModified] = useState(false);
  const [configModified, setConfigModified] = useState(false);

  // Configuration state
  const [selectedFamily, setSelectedFamily] = useState<string | null>(null);
  const [configurationOpen, setConfigurationOpen] = useState(false);
  const [addTasks, setAddTasks] = useState(DEFAULT_CONFIGURATION.includeTasks);
  const [addFacts, setAddFacts] = useState(DEFAULT_CONFIGURATION.includeFacts);
  const [addNotes, setAddNotes] = useState(DEFAULT_CONFIGURATION.includeNotes);
  const [addTaskActivity, setAddTaskActivity] = useState(
    DEFAULT_CONFIGURATION.includeTaskActivity,
  );
  const [eventsWeeksForward, setEventsWeeksForward] = useState<number>(
    DEFAULT_CONFIGURATION.eventsWeeksForward,
  );
  const [activityWeeksBack, setActivityWeeksBack] = useState<number>(
    DEFAULT_CONFIGURATION.activityWeeksBack,
  );
  const [selectedModel, setSelectedModel] = useState<string>("");
  const [selectedDocument, setSelectedDocument] =
    useState<LoadDocumentMetadata | null>(null);
  const [documentId, setDocumentId] = useState<string>("");

  // Run state
  const [loading, setLoading] = useState(false);
  const [output, setOutput] = useState<string>("");
  const [pendingAutoRun, setPendingAutoRun] = useState(false);
  const [documentData, setDocumentData] = useState<{
    content: string;
    contentType: string;
    title: string;
  } | null>(null);

  // Menu state
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  // Comments state
  const [commentCount, setCommentCount] = useState(0);
  const showComments = searchParams.has("comments");

  // API hooks
  const { request: listAgentPrompts } = useListAgentPrompts();
  const { request: createAgentPrompt, loading: createLoading } =
    useCreateAgentPrompt();
  const { request: updateAgentPrompt, loading: updateLoading } =
    useUpdateAgentPrompt();
  const { request: toggleAgentPrompt } = useToggleAgentPrompt();
  const { request: workflowRequest } = useRunWorkflow();
  const { request: createDocumentRequest, loading: createDocumentLoading } =
    useCreateDocument();

  const isOwner = selectedPrompt?.owner?.ref === currentUser.ref;
  const canEdit = isAdmin || isOwner;

  useEffect(() => {
    const initPrompts = async () => {
      const response = await listAgentPrompts(
        new ListAgentPromptsRequest({ type: "family-prompt" }),
      );
      if (response) {
        setPrompts(response.prompts);
        const promptRef = searchParams.get("prompt");
        if (promptRef) {
          const prompt = response.prompts.find((p) => p.ref === promptRef);
          if (prompt) {
            handleSelectPrompt(prompt);
          }
        }
      }
    };
    initPrompts();

    const familyRef = searchParams.get("family");
    if (familyRef) {
      setSelectedFamily(familyRef);
    }

    if (searchParams.get("autoRun") === "1") {
      setPendingAutoRun(true);
    }
  }, []);

  const loadPrompts = async () => {
    const response = await listAgentPrompts(
      new ListAgentPromptsRequest({ type: "family-prompt" }),
    );
    if (response) {
      setPrompts(response.prompts);
    }
  };

  const applyConfiguration = useCallback(
    (config: PromptConfiguration | null) => {
      const c = config || DEFAULT_CONFIGURATION;
      setAddTasks(c.includeTasks ?? DEFAULT_CONFIGURATION.includeTasks);
      setAddFacts(c.includeFacts ?? DEFAULT_CONFIGURATION.includeFacts);
      setAddNotes(c.includeNotes ?? DEFAULT_CONFIGURATION.includeNotes);
      setAddTaskActivity(
        c.includeTaskActivity ?? DEFAULT_CONFIGURATION.includeTaskActivity,
      );
      setEventsWeeksForward(
        c.eventsWeeksForward ?? DEFAULT_CONFIGURATION.eventsWeeksForward,
      );
      // Handle legacy communicationDaysBack by converting to weeks
      const weeksBack =
        c.activityWeeksBack ??
        (c.communicationDaysBack ? Math.round(c.communicationDaysBack / 7) : 0);
      setActivityWeeksBack(
        weeksBack || DEFAULT_CONFIGURATION.activityWeeksBack,
      );
      setSelectedModel(c.model || "");
      setDocumentId(c.documentId || "");
      setSelectedDocument(null);
      setConfigModified(false);
    },
    [],
  );

  const handleSelectPrompt = useCallback(
    (prompt: AgentPrompt) => {
      setSelectedPrompt(prompt);
      setPromptText(prompt.content?.content?.textContent || "");
      setPromptName(prompt.name);
      setPromptScope(prompt.scope || "public");
      setPromptModified(false);
      setConfigModified(false);
      setConfigurationOpen(false);
      setOutput("");
      setDocumentData(null);

      const config = prompt.configurationJson
        ? (JSON.parse(prompt.configurationJson) as PromptConfiguration)
        : null;
      applyConfiguration(config);

      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set("prompt", prompt.ref);
          return newParams;
        },
        { replace: true },
      );
    },
    [applyConfiguration, setSearchParams],
  );

  const handleSelectExample = (example: (typeof EXAMPLE_PROMPTS)[0]) => {
    setSelectedPrompt(null);
    setPromptText(example.prompt);
    setPromptName("");
    setPromptScope("public");
    setPromptModified(true);
    setConfigModified(false);
    setConfigurationOpen(false);
    setOutput("");
    setDocumentData(null);
    applyConfiguration(null);
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete("prompt");
        return newParams;
      },
      { replace: true },
    );
  };

  const handleClearSelection = () => {
    setSelectedPrompt(null);
    setPromptText("");
    setPromptName("");
    setPromptScope("public");
    setPromptModified(false);
    setConfigModified(false);
    setConfigurationOpen(false);
    setOutput("");
    setDocumentData(null);
    applyConfiguration(null);
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete("prompt");
        return newParams;
      },
      { replace: true },
    );
  };

  const checkIfModified = (text: string, name: string) => {
    if (selectedPrompt) {
      const textChanged = text !== selectedPrompt.content?.content?.textContent;
      const nameChanged = name !== selectedPrompt.name;
      setPromptModified(textChanged || nameChanged);
    } else {
      setPromptModified(text.trim().length > 0);
    }
  };

  const handlePromptTextChange = (text: string) => {
    setPromptText(text);
    checkIfModified(text, promptName);
  };

  const handlePromptNameChange = (name: string) => {
    setPromptName(name);
    checkIfModified(promptText, name);
  };

  const handleScopeChange = async (newScope: string) => {
    if (!selectedPrompt) return;
    setPromptScope(newScope);

    const response = await updateAgentPrompt(
      new UpdateAgentPromptRequest({
        ref: selectedPrompt.ref,
        type: "family-prompt",
        name: selectedPrompt.name,
        status: selectedPrompt.status,
        scope: newScope,
        updateConfiguration: true,
      }),
    );

    if (response) {
      await loadPrompts();
      pushSnack("Scope updated");
    }
  };

  const handleFamilyChange = (familyRef: string | null) => {
    setSelectedFamily(familyRef);
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        if (familyRef) {
          newParams.set("family", familyRef);
        } else {
          newParams.delete("family");
        }
        return newParams;
      },
      { replace: true },
    );
  };

  const handleRun = async () => {
    if (!selectedFamily || !promptText.trim() || loading) return;

    setLoading(true);
    setOutput("");
    setDocumentData(null);

    try {
      const request = new RunWorkflowRequest({
        workflowName: "family-context-prompted",
        arguments: [
          {
            name: "communication_days_back",
            value: {
              case: "intValue",
              value: activityWeeksBack * 7,
            },
          },
          {
            name: "include_tasks",
            value: { case: "boolValue", value: addTasks },
          },
          {
            name: "include_facts",
            value: { case: "boolValue", value: addFacts },
          },
          {
            name: "include_notes",
            value: { case: "boolValue", value: addNotes },
          },
          {
            name: "include_task_activity",
            value: { case: "boolValue", value: addTaskActivity },
          },
          {
            name: "events_days_forward",
            value: { case: "intValue", value: eventsWeeksForward * 7 },
          },
          {
            name: "extra_context",
            value: { case: "stringValue", value: promptText },
          },
          ...(selectedModel && isAdmin
            ? [
                {
                  name: "model",
                  value: { case: "stringValue" as const, value: selectedModel },
                },
              ]
            : []),
          ...(selectedDocument
            ? [
                {
                  name: "output_template",
                  value: {
                    case: "stringValue" as const,
                    value: `documents:${selectedDocument.id}?use_rich=true`,
                  },
                },
              ]
            : []),
        ],
        entityType: 0,
        entityRef: selectedFamily,
        runAsync: false,
      });

      const result = await runWorkflowWithPolling(workflowRequest, request);
      if (result) {
        const json = JSON.parse(result);

        if (
          json.document_content &&
          json.document_content_type &&
          json.document_title
        ) {
          setDocumentData({
            content: json.document_content,
            contentType: json.document_content_type,
            title: json.document_title,
          });
          setOutput("");
        } else {
          setDocumentData(null);
          setOutput(json.response || json.prompt || result);
        }
      }
    } catch (err) {
      setOutput("Error running prompt");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (pendingAutoRun && selectedFamily && promptText.trim() && !loading) {
      setPendingAutoRun(false);
      handleRun();
    }
  }, [pendingAutoRun, selectedFamily, promptText, loading]);

  const handleSaveClick = () => {
    if (selectedPrompt) {
      handleUpdatePrompt();
    } else {
      handleCreatePrompt();
    }
  };

  const buildConfigurationJson = (): string => {
    const config: PromptConfiguration = {
      includeTasks: addTasks,
      includeFacts: addFacts,
      includeNotes: addNotes,
      includeTaskActivity: addTaskActivity,
      eventsWeeksForward: eventsWeeksForward,
      activityWeeksBack: activityWeeksBack,
      ...(selectedModel && isAdmin ? { model: selectedModel } : {}),
      ...(documentId ? { documentId } : {}),
    };
    return JSON.stringify(config);
  };

  const handleUpdatePrompt = async () => {
    if (!selectedPrompt || !promptName.trim()) return;

    const response = await updateAgentPrompt(
      new UpdateAgentPromptRequest({
        ref: selectedPrompt.ref,
        type: "family-prompt",
        name: promptName.trim(),
        status: selectedPrompt.status,
        content: {
          contentType: "html",
          payload: promptText,
          textContent: promptText,
          attachments: [],
        },
        scope: promptScope,
        configurationJson: buildConfigurationJson(),
        updateConfiguration: true,
      }),
    );

    if (response) {
      await loadPrompts();
      setPromptModified(false);
      setConfigModified(false);
      pushSnack("Prompt updated");
    }
  };

  const handleCreatePrompt = async () => {
    if (!promptName.trim()) {
      pushSnack("Please enter a title for your prompt");
      return;
    }

    const response = await createAgentPrompt({
      type: "family-prompt",
      name: promptName.trim(),
      status: "active",
      scope: "public",
      content: {
        contentType: "html",
        payload: promptText,
        textContent: promptText,
        attachments: [],
      },
      configurationJson: buildConfigurationJson(),
      clientOutputVisible: false,
    });

    if (response?.prompt) {
      const newPrompt = response.prompt;
      await toggleAgentPrompt({
        promptRef: newPrompt.ref,
        isEnabled: true,
      });
      await loadPrompts();
      setSelectedPrompt(newPrompt);
      setPromptName(newPrompt.name);
      setPromptModified(false);
      setConfigModified(false);
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set("prompt", newPrompt.ref);
          return newParams;
        },
        { replace: true },
      );
      pushSnack("Prompt created");
    }
  };

  const handleDeletePrompt = async () => {
    if (!selectedPrompt) return;
    if (!window.confirm("Are you sure you want to delete this prompt?")) return;

    const response = await updateAgentPrompt(
      new UpdateAgentPromptRequest({
        ref: selectedPrompt.ref,
        type: "family-prompt",
        status: "disabled",
      }),
    );

    if (response) {
      await loadPrompts();
      handleClearSelection();
      pushSnack("Prompt deleted");
    }
  };

  const handleTogglePromptEnabled = async (
    promptRef: string,
    isEnabled: boolean,
  ) => {
    await toggleAgentPrompt({
      promptRef,
      isEnabled,
    });
    await loadPrompts();
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(output);
      pushSnack("Copied to clipboard");
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const handleDocumentCopy = async () => {
    try {
      if (documentData) {
        if (documentData.contentType === "html") {
          const element = document.querySelector(
            "#document-viewer .ProseMirror",
          );
          const plainText = element?.textContent || "";

          const clipboardItem = new ClipboardItem({
            "text/plain": new Blob([plainText], { type: "text/plain" }),
            "text/html": new Blob([documentData.content], {
              type: "text/html",
            }),
          });

          await navigator.clipboard.write([clipboardItem]);
        } else {
          await navigator.clipboard.writeText(documentData.content);
        }
        pushSnack("Copied to clipboard");
      }
    } catch (err) {
      console.error("Failed to copy document: ", err);
    }
  };

  const handleDocumentEdit = async () => {
    if (!documentData || !selectedFamily) return;
    const doc = await createDocumentRequest({
      familyRef: selectedFamily,
      title: documentData.title,
      textContent: documentData.content,
      content: documentData.content,
      attachments: [],
      documentType: DocumentType.NOTE,
      topics: [],
    });
    if (doc) {
      navigate(`/documents/${encodeURIComponent(doc.document?.uuid || "")}`);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([output], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `prompt-output-${new Date().toISOString().split("T")[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const showExamples = !selectedPrompt && !promptText.trim();

  const getOwnerDisplay = (prompt: AgentPrompt) => {
    const ownerName = prompt.owner?.displayName || "";
    if (
      ownerName.toLowerCase() === "faye" ||
      ownerName.toLowerCase() === "faye admin"
    ) {
      return "Faye";
    }
    return ownerName;
  };

  const leftPanelContent = (
    <Box
      sx={{
        padding: isMobile ? "20px" : "32px 48px",
        overflow: "auto",
        height: "100%",
      }}
    >
      <Box
        sx={{
          margin: isMobile ? "" : "0 auto",
          maxWidth: "1200px",
        }}
      >
        {/* Page Title */}
        <Box sx={{ marginBottom: "24px" }}>
          <Typography
            variant="title"
            sx={{ display: "flex", alignItems: "center" }}
          >
            <SparklesIcon
              height="24px"
              width="24px"
              style={{ marginRight: "8px" }}
            />
            Magic Docs
          </Typography>
          <Typography variant="body">
            Use AI to generate personalized documents for your families.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: "32px",
            minHeight: "calc(100vh - 280px)",
          }}
        >
          {/* Left Sidebar - Prompts List */}
          <Box
            sx={{
              width: isMobile ? "100%" : "340px",
              flexShrink: 0,
              display: isMobile ? "none" : "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                border: "1px solid #EAEBEC",
                borderRadius: "8px",
                background: "#FFF",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                flex: 1,
              }}
            >
              {/* List Header */}
              <Box
                sx={{
                  padding: "12px 16px",
                  borderBottom: "1px solid #EAEBEC",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexShrink: 0,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Users
                    color="#198282"
                    height="18px"
                    width="18px"
                    style={{ marginRight: "8px" }}
                  />
                  <Typography variant="bodyHeavy" sx={{ fontSize: "14px" }}>
                    Saved Prompts
                  </Typography>
                </Box>
                <Tooltip title="New prompt">
                  <IconButton
                    size="small"
                    onClick={handleClearSelection}
                    sx={{ padding: "4px" }}
                  >
                    <SquarePen size={18} color="#198282" />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Prompts List */}
              <Box sx={{ flex: 1, overflow: "auto" }}>
                {prompts.length === 0 ? (
                  <Box sx={{ padding: "20px", textAlign: "center" }}>
                    <Typography variant="bodySmall" color="#667085">
                      No saved prompts yet
                    </Typography>
                  </Box>
                ) : (
                  prompts.map((prompt, index) => (
                    <Box
                      key={prompt.ref}
                      sx={{
                        padding: "12px 16px",
                        cursor: "pointer",
                        borderBottom:
                          index < prompts.length - 1
                            ? "1px solid #EAEBEC"
                            : "none",
                        backgroundColor: "transparent",
                        "&:hover": {
                          backgroundColor: "#FAFAFA",
                        },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "8px",
                      }}
                    >
                      <Box
                        onClick={() => handleSelectPrompt(prompt)}
                        sx={{ flex: 1, minWidth: 0 }}
                      >
                        <Typography
                          variant="bodyHeavy"
                          sx={{ fontSize: "14px", marginBottom: "2px" }}
                        >
                          {prompt.name}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            marginBottom: "4px",
                          }}
                        >
                          <Typography
                            variant="bodySmall"
                            sx={{ color: "#667085", fontSize: "11px" }}
                          >
                            {getOwnerDisplay(prompt)} •
                          </Typography>
                          <DateDisplay
                            date={new Date(Number(prompt.creationSec) * 1000)}
                            sx={{ fontSize: "11px", color: "#667085" }}
                          />
                        </Box>
                        <Typography
                          variant="bodySmall"
                          sx={{ color: "#667085", fontSize: "12px" }}
                        >
                          <TruncatedText lines={2}>
                            {prompt.content?.content?.textContent ||
                              "No content"}
                          </TruncatedText>
                        </Typography>
                      </Box>
                      <Switch
                        size="small"
                        checked={prompt.isEnabledForUser}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleTogglePromptEnabled(
                            prompt.ref,
                            e.target.checked,
                          );
                        }}
                        sx={{ flexShrink: 0, marginTop: "-4px" }}
                      />
                    </Box>
                  ))
                )}
              </Box>
            </Box>
          </Box>

          {/* Main Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Header */}
            <FlexBox
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ marginBottom: "16px" }}
            >
              <Box flex="1">
                {(selectedPrompt && canEdit) || !selectedPrompt ? (
                  <MuiTextField
                    value={promptName}
                    onChange={(e) => handlePromptNameChange(e.target.value)}
                    fullWidth
                    variant="standard"
                    placeholder="Untitled prompt"
                    margin="none"
                    InputProps={{
                      disableUnderline: true,
                      sx: (theme) => ({
                        ...theme.typography.bodyHeavy,
                        fontSize: "18px",
                        input: {
                          padding: "0 0 0",
                        },
                      }),
                    }}
                    sx={{ marginBottom: "4px" }}
                  />
                ) : (
                  // shouldn't be editable
                  <Typography
                    variant="bodyHeavy"
                    sx={{ fontSize: "18px", marginBottom: "4px" }}
                  >
                    {promptName}
                  </Typography>
                )}
                {selectedPrompt && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <Typography variant="body" color="#667085">
                      Created by {getOwnerDisplay(selectedPrompt)} •
                    </Typography>
                    <DateDisplay
                      date={new Date(Number(selectedPrompt.creationSec) * 1000)}
                      sx={{ color: "#667085" }}
                    />
                    <Typography variant="body" color="#667085">
                      •
                    </Typography>
                    <Chip
                      size="small"
                      label={
                        selectedPrompt.isEnabledForUser
                          ? "Available for your families"
                          : "Disabled for your families"
                      }
                      sx={{
                        height: "20px",
                        fontSize: "12px",
                        backgroundColor: selectedPrompt.isEnabledForUser
                          ? "#E8F5E9"
                          : "#FAFAFA",
                        color: selectedPrompt.isEnabledForUser
                          ? "#2E7D32"
                          : "#667085",
                      }}
                    />
                  </Box>
                )}
              </Box>
              <Box display="flex" gap="8px" alignItems="center">
                {(promptModified || (configModified && selectedPrompt)) &&
                  (!selectedPrompt || canEdit) && (
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<Save size={16} />}
                      onClick={handleSaveClick}
                      disabled={loading || createLoading || updateLoading}
                    >
                      {selectedPrompt ? "Save" : "Create"}
                    </Button>
                  )}
                {selectedPrompt && canEdit && (
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                      value={promptScope}
                      onChange={(e) => handleScopeChange(e.target.value)}
                      size="small"
                      disabled={updateLoading}
                      sx={{ fontSize: "14px" }}
                    >
                      <MenuItem value="private">Private</MenuItem>
                      <MenuItem value="public">Public</MenuItem>
                      {isAdmin && (
                        <MenuItem value="system-default">
                          System Default
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                )}
                {selectedPrompt && canEdit && (
                  <>
                    <IconButton
                      size="small"
                      onClick={(e) => setMenuAnchor(e.currentTarget)}
                      sx={{ padding: "4px" }}
                    >
                      <MoreVertical size={18} />
                    </IconButton>
                    <Menu
                      anchorEl={menuAnchor}
                      open={Boolean(menuAnchor)}
                      onClose={() => setMenuAnchor(null)}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                    >
                      {canEdit && (
                        <MenuItem
                          onClick={() => {
                            setMenuAnchor(null);
                            handleDeletePrompt();
                          }}
                          sx={{ color: "#d32f2f" }}
                        >
                          <Trash2 size={16} style={{ marginRight: "8px" }} />
                          Delete
                        </MenuItem>
                      )}
                    </Menu>
                  </>
                )}
              </Box>
            </FlexBox>

            {/* Configuration */}
            <FlexBox
              direction="column"
              gap="16px"
              sx={{ marginBottom: "16px" }}
            >
              <Box
                sx={{
                  border: "1px solid #D4D4D4",
                  borderRadius: "12px",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    padding: "16px 20px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    backgroundColor: configurationOpen
                      ? "#f9f9f9"
                      : "transparent",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                  onClick={() => setConfigurationOpen(!configurationOpen)}
                >
                  <Typography variant="bodyHeavy">Advanced</Typography>
                  <ChevronDown
                    size={20}
                    style={{
                      transform: configurationOpen
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                      transition: "transform 0.2s ease-in-out",
                    }}
                  />
                </Box>
                <Collapse in={configurationOpen}>
                  <FlexBox
                    direction="column"
                    sx={{
                      padding: "20px",
                    }}
                  >
                    <ConfigurationSection
                      title="Information sources"
                      content={
                        <FormControl fullWidth>
                          <InputLabel>Sources</InputLabel>
                          <MuiSelect
                            multiple
                            value={[
                              ...(addTasks ? ["tasks"] : []),
                              ...(addTaskActivity ? ["taskActivity"] : []),
                              ...(addFacts ? ["facts"] : []),
                              ...(addNotes ? ["notes"] : []),
                            ]}
                            onChange={(e) => {
                              const values = e.target.value as string[];
                              setAddTasks(values.includes("tasks"));
                              setAddTaskActivity(
                                values.includes("taskActivity"),
                              );
                              setAddFacts(values.includes("facts"));
                              setAddNotes(values.includes("notes"));
                              setConfigModified(true);
                            }}
                            disabled={loading}
                            label="Sources"
                            renderValue={(selected) => {
                              const labels: Record<string, string> = {
                                tasks: "Tasks",
                                taskActivity: "Task Activity",
                                facts: "Facts",
                                notes: "Notes",
                              };
                              return (selected as string[])
                                .map((v) => labels[v])
                                .join(", ");
                            }}
                          >
                            {[
                              { value: "tasks", label: "Tasks" },
                              {
                                value: "taskActivity",
                                label: "Task Activity",
                              },
                              { value: "facts", label: "Facts" },
                              { value: "notes", label: "Notes" },
                            ].map((item) => (
                              <MenuItem key={item.value} value={item.value}>
                                <MuiCheckbox
                                  checked={
                                    [
                                      ...(addTasks ? ["tasks"] : []),
                                      ...(addTaskActivity
                                        ? ["taskActivity"]
                                        : []),
                                      ...(addFacts ? ["facts"] : []),
                                      ...(addNotes ? ["notes"] : []),
                                    ].indexOf(item.value) > -1
                                  }
                                  size="small"
                                />
                                <ListItemText primary={item.label} />
                              </MenuItem>
                            ))}
                          </MuiSelect>
                        </FormControl>
                      }
                    />

                    <ConfigurationSection
                      title="Time period"
                      content={
                        <FlexBox stackedMobile>
                          <FormControl sx={{ flex: 1 }}>
                            <InputLabel>Events weeks forward</InputLabel>
                            <MuiSelect
                              value={eventsWeeksForward}
                              onChange={(e) => {
                                setEventsWeeksForward(Number(e.target.value));
                                setConfigModified(true);
                              }}
                              disabled={loading}
                              label="Events weeks forward"
                            >
                              <MenuItem value={0}>No events</MenuItem>
                              <MenuItem value={1}>1 week</MenuItem>
                              <MenuItem value={2}>2 weeks</MenuItem>
                              <MenuItem value={4}>3 weeks</MenuItem>
                              <MenuItem value={8}>1 month</MenuItem>
                              <MenuItem value={12}>2 months</MenuItem>
                              <MenuItem value={16}>3 months</MenuItem>
                              <MenuItem value={24}>6 months</MenuItem>
                            </MuiSelect>
                          </FormControl>

                          <FormControl sx={{ flex: 1 }}>
                            <InputLabel>Activity weeks back</InputLabel>
                            <MuiSelect
                              value={activityWeeksBack}
                              onChange={(e) => {
                                setActivityWeeksBack(Number(e.target.value));
                                setConfigModified(true);
                              }}
                              disabled={loading}
                              label="Activity weeks back"
                            >
                              <MenuItem value={1}>1 week</MenuItem>
                              <MenuItem value={2}>2 weeks</MenuItem>
                              <MenuItem value={4}>4 weeks</MenuItem>
                              <MenuItem value={8}>2 months</MenuItem>
                              <MenuItem value={12}>3 months</MenuItem>
                              <MenuItem value={24}>6 months</MenuItem>
                              <MenuItem value={52}>1 year</MenuItem>
                            </MuiSelect>
                          </FormControl>
                        </FlexBox>
                      }
                    />
                    <ConfigurationSection
                      title="Document template"
                      content={
                        <FlexBox direction="column" gap="8px">
                          <LoadDocument
                            onDocumentSelect={(doc) => {
                              setSelectedDocument(doc);
                              setDocumentId(doc?.id || "");
                              setConfigModified(true);
                            }}
                            disabled={loading}
                            initialDocumentId={documentId}
                            listOptions={true}
                            // topics={["Template"]}
                          />
                          <Typography
                            variant="bodySmall"
                            sx={{ color: "#667085" }}
                          >
                            Output will follow this document's structure. Use a
                            document accessible to all advisors if sharing this
                            prompt.
                          </Typography>
                        </FlexBox>
                      }
                    />

                    {isAdmin && (
                      <>
                        <Divider sx={{ width: "100%", margin: "12px 0 8px" }} />
                        <ConfigurationSection
                          title="Model"
                          content={
                            <FormControl fullWidth>
                              <InputLabel>Model</InputLabel>
                              <MuiSelect
                                value={selectedModel}
                                onChange={(e) => {
                                  setSelectedModel(e.target.value as string);
                                  setConfigModified(true);
                                }}
                                disabled={loading}
                                label="Model"
                              >
                                <MenuItem value="">Auto</MenuItem>
                                {MODELS.map((model) => (
                                  <MenuItem key={model.name} value={model.name}>
                                    {model.label}
                                  </MenuItem>
                                ))}
                              </MuiSelect>
                            </FormControl>
                          }
                        />
                      </>
                    )}
                  </FlexBox>
                </Collapse>
              </Box>
            </FlexBox>

            {/* Prompt Text Area */}
            <Box sx={{ position: "relative", marginBottom: "16px" }}>
              <MuiTextField
                fullWidth
                multiline
                minRows={8}
                variant="outlined"
                value={promptText}
                onChange={(e) => handlePromptTextChange(e.target.value)}
                placeholder="Write a prompt describing what you want to generate for your client..."
                disabled={loading}
                InputProps={{
                  sx: {
                    paddingBottom: "64px",
                  },
                }}
              />
              {/* Examples Section */}
              <Fade
                in={showExamples}
                timeout={1000}
                unmountOnExit
                style={{ width: "100%" }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 76,
                    gap: "8px",
                    marginBottom: "24px",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <FlexBox
                    gap="12px"
                    sx={{ flexWrap: "wrap", justifyContent: "center" }}
                  >
                    {EXAMPLE_PROMPTS.map((example, index) => (
                      <Chip
                        key={index}
                        variant="outlined"
                        label={example.title}
                        onClick={() => handleSelectExample(example)}
                      />
                    ))}
                  </FlexBox>
                </Box>
              </Fade>
              <Box
                sx={{
                  position: "absolute",
                  bottom: 12,
                  padding: "0 12px",
                  display: "flex",
                  gap: "8px",
                  alignItems: "center",
                  width: "100%",
                  justifyContent: "space-between",
                }}
              >
                {isAdmin ? (
                  <AdminFamilySelect
                    selectedFamily={selectedFamily}
                    onChange={handleFamilyChange}
                    title="Select Family"
                    disabled={loading}
                    sx={{
                      maxWidth: "200px",
                      ".MuiOutlinedInput-notchedOutline": {
                        border: "none !important",
                      },
                      ".MuiSelect-select": { paddingRight: "32px !important" },
                    }}
                  />
                ) : (
                  <FamilySelect
                    selectedFamily={selectedFamily}
                    onChange={handleFamilyChange}
                    disabled={loading}
                    sx={{
                      width: "auto",
                      maxWidth: "200px",
                      ".MuiOutlinedInput-notchedOutline": {
                        border: "none !important",
                      },
                      ".MuiSelect-icon": { right: "8px" },
                    }}
                  />
                )}
                <ThreeDotsActiveButton
                  size="small"
                  onClick={handleRun}
                  disabled={!selectedFamily || !promptText.trim() || loading}
                  active={loading}
                >
                  Run prompt
                </ThreeDotsActiveButton>
              </Box>
            </Box>

            {/* Document Output */}
            {documentData && (
              <Box
                sx={{
                  border: "1px solid #EAEBEC",
                  borderRadius: "8px",
                  padding: "16px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "12px",
                  }}
                >
                  <Typography variant="bodyHeavy">
                    {documentData.title}
                  </Typography>
                  <Box sx={{ display: "flex", gap: "4px" }}>
                    <Tooltip title="Copy content">
                      <IconButton
                        size="small"
                        onClick={handleDocumentCopy}
                        sx={{
                          padding: "4px",
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                      >
                        <Copy size={16} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Create & edit document">
                      <IconButton
                        size="small"
                        onClick={handleDocumentEdit}
                        disabled={createDocumentLoading}
                        sx={{
                          padding: "4px",
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                      >
                        <Pencil size={16} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <Divider sx={{ marginBottom: "16px" }} />
                {documentData.contentType === "text" ? (
                  <Typography
                    variant="body"
                    sx={{
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {documentData.content}
                  </Typography>
                ) : (
                  <div id="document-viewer">
                    <DocumentViewer
                      content={documentData.content}
                      maxHeight="400px"
                    />
                  </div>
                )}
              </Box>
            )}

            {/* Text Output */}
            {output && (
              <Box
                sx={{
                  padding: "16px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "8px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <Typography variant="bodyHeavy">Output</Typography>
                  <Box sx={{ display: "flex", gap: "4px" }}>
                    <Tooltip title="Copy to clipboard">
                      <IconButton
                        size="small"
                        onClick={handleCopy}
                        sx={{
                          padding: "4px",
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                      >
                        <Copy size={16} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download as text file">
                      <IconButton
                        size="small"
                        onClick={handleDownload}
                        sx={{
                          padding: "4px",
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                      >
                        <Download size={16} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <Typography
                  variant="body"
                  component="pre"
                  sx={{
                    whiteSpace: "pre-wrap",
                    fontFamily: "monospace",
                  }}
                >
                  {output}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const rightPanelContent = selectedPrompt ? (
    <MiniFeed
      entityType={ActivityEntityType.FAMILY_PROMPT_CREATED}
      entityRef={selectedPrompt.ref}
      hideHeader={true}
      onCommentsLoaded={setCommentCount}
    />
  ) : null;

  return (
    <SplitPanel
      leftPanel={leftPanelContent}
      rightPanel={rightPanelContent}
      rightPanelIcon={<MessagesSquare size={20} />}
      rightPanelTitle={`Comments (${commentCount})`}
      leftPanelMaxWidth="1200px"
      defaultCollapsed={!showComments}
    />
  );
};
